import React from 'react';
import { useTheme } from '../../hooks/useTheme';

const Card = ({ 
  children, 
  title, 
  subtitle,
  variant = 'default', 
  padding = 'lg',
  hoverable = false,
  className = '',
  onClick,
  isDarkMode = false,
  ...props 
}) => {
  const theme = useTheme();
  const baseStyles = {
    backgroundColor: theme.colors.secondary,
    border: `1px solid ${theme.colors.border.primary}`,
    borderRadius: theme.borderRadius.lg,
    fontFamily: theme.typography.fontFamily.primary,
    transition: 'all 0.2s ease-in-out',
    boxSizing: 'border-box',
    cursor: onClick ? 'pointer' : 'default',
    ...props.style
  };

  const variantStyles = {
    default: {
      backgroundColor: theme.colors.secondary,
      borderColor: theme.colors.border.primary,
    },
    elevated: {
      backgroundColor: theme.colors.tertiary,
      borderColor: theme.colors.border.secondary,
      boxShadow: theme.colors.shadow.md,
    },
    outlined: {
      backgroundColor: 'transparent',
      borderColor: theme.colors.border.primary,
      borderWidth: '2px',
    },
    filled: {
      backgroundColor: theme.colors.tertiary,
      borderColor: theme.colors.tertiary,
    }
  };

  const paddingStyles = {
    sm: { padding: theme.spacing.md },
    md: { padding: theme.spacing.lg },
    lg: { padding: theme.spacing.xl },
    xl: { padding: theme.spacing['2xl'] },
    none: { padding: 0 }
  };

  const headerStyles = {
    marginBottom: theme.spacing.md,
    paddingBottom: theme.spacing.md,
    borderBottom: `1px solid ${theme.colors.border.primary}`,
  };

  const titleStyles = {
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.colors.text.primary,
    margin: 0,
    marginBottom: subtitle ? theme.spacing.xs : 0,
  };

  const subtitleStyles = {
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.normal,
    color: theme.colors.text.secondary,
    margin: 0,
  };

  const contentStyles = {
    color: theme.colors.text.primary,
  };

  const handleMouseEnter = (e) => {
    if (hoverable || onClick) {
      e.currentTarget.style.backgroundColor = theme.colors.hover;
      e.currentTarget.style.borderColor = theme.colors.border.secondary;
      e.currentTarget.style.transform = 'translateY(-1px)';
      e.currentTarget.style.boxShadow = theme.colors.shadow.lg;
    }
  };

  const handleMouseLeave = (e) => {
    if (hoverable || onClick) {
      e.currentTarget.style.backgroundColor = variantStyles[variant].backgroundColor;
      e.currentTarget.style.borderColor = variantStyles[variant].borderColor;
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = variantStyles[variant].boxShadow || 'none';
    }
  };

  const combinedStyles = {
    ...baseStyles,
    ...variantStyles[variant],
    ...paddingStyles[padding]
  };

  return (
    <div
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={className}
      style={combinedStyles}
      {...props}
    >
      {(title || subtitle) && (
        <div style={headerStyles}>
          {title && <h3 style={titleStyles}>{title}</h3>}
          {subtitle && <p style={subtitleStyles}>{subtitle}</p>}
        </div>
      )}
      <div style={contentStyles}>
        {children}
      </div>
    </div>
  );
};

// Card Header Component
export const CardHeader = ({ children, className = '', isDarkMode = false, ...props }) => {
  const theme = useTheme();
  const headerStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.md,
    paddingBottom: theme.spacing.md,
    borderBottom: `1px solid ${theme.colors.border.primary}`,
    ...props.style
  };

  return (
    <div className={className} style={headerStyles} {...props}>
      {children}
    </div>
  );
};

// Card Body Component
export const CardBody = ({ children, className = '', isDarkMode = false, ...props }) => {
  const theme = useTheme();
  const bodyStyles = {
    color: theme.colors.text.primary,
    ...props.style
  };

  return (
    <div className={className} style={bodyStyles} {...props}>
      {children}
    </div>
  );
};

// Card Footer Component
export const CardFooter = ({ children, className = '', isDarkMode = false, ...props }) => {
  const theme = useTheme();
  const footerStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing.md,
    paddingTop: theme.spacing.md,
    borderTop: `1px solid ${theme.colors.border.primary}`,
    ...props.style
  };

  return (
    <div className={className} style={footerStyles} {...props}>
      {children}
    </div>
  );
};

export default Card;
